import React from 'react';
import { Link } from 'react-router-dom';
import type { Lead } from '../../services/leadSyncService';
import { StatusBadge, RelatedLinks, type RelatedLinkItem } from '../../components/ui';
import { Icon } from '../../components/icons';
import { datum } from '../format';
import { leadName, leadStatus, lemlistStatus, quelleLabel, tagsAus } from './leadStatus';

interface Props {
  leads: Lead[];
  ausgewaehlt: Set<string>;
  onToggle: (id: string) => void;
  onToggleAlle: () => void;
  /** Fehlt der Handler (kein Recht vertrieb.bearbeiten, Welle 2), gibt es den Knopf nicht. */
  onBearbeiten?: (lead: Lead) => void;
  /** Fehlt der Handler (kein Recht versand.ausfuehren), gibt es den Knopf nicht. */
  onEntfernen?: (lead: Lead) => void;
  /** Fehlt der Handler (kein Recht vertrieb.loeschen), gibt es den Knopf nicht. */
  onLoeschen?: (lead: Lead) => void;
  kampagnenName: (id: string) => string;
}

/** Vorgaenge und ICP-Kampagnen des Leads als Sprung-Chips. */
function leadVerweise(l: Lead): RelatedLinkItem[] {
  return [
    ...(l.deals ?? []).map((d) => ({
      key: `deal-${d.id}`, label: d.title, icon: 'kanban' as const, to: `/pipeline?deal=${encodeURIComponent(d.id)}`,
      title: `Vorgang „${d.title}“ (${d.status === 'won' ? 'gewonnen' : d.status === 'lost' ? 'verloren' : 'offen'}) öffnen`,
    })),
    ...(l.icpCampaignProspects ?? []).map(({ campaign }) => ({
      key: `icp-${campaign.id}`, label: campaign.name, icon: 'target' as const, to: `/icp-campaigns/${encodeURIComponent(campaign.id)}`,
      title: `ICP-Kampagne „${campaign.name}“ öffnen`,
    })),
  ];
}

export const LeadTabelle: React.FC<Props> = ({
  leads, ausgewaehlt, onToggle, onToggleAlle, onBearbeiten, onEntfernen, onLoeschen, kampagnenName,
}) => {
  const alle = leads.length > 0 && leads.every((l) => ausgewaehlt.has(l.id));
  return (
    <div className="table-container">
      <table className="table lead-tabelle">
        <thead>
          <tr>
            <th className="lead-tabelle__wahl">
              <input type="checkbox" checked={alle} onChange={onToggleAlle} aria-label="Alle sichtbaren Leads auswählen" />
            </th>
            <th>Lead</th>
            <th>Firma</th>
            <th>Status</th>
            <th>Kampagne</th>
            <th>Quelle</th>
            <th>Angelegt</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => {
            const st = leadStatus(l.status);
            const lem = lemlistStatus(l.lemlistStatus);
            const tags = tagsAus(l);
            return (
              <tr key={l.id} data-focus-id={l.id} className={ausgewaehlt.has(l.id) ? 'zeile-gewaehlt' : ''}>
                <td className="lead-tabelle__wahl">
                  <input type="checkbox" checked={ausgewaehlt.has(l.id)} onChange={() => onToggle(l.id)} aria-label={`${leadName(l)} auswählen`} />
                </td>
                <td>
                  <strong>{leadName(l)}</strong>
                  <div className="text-xs text-secondary">{l.email}{l.jobTitle && <> · {l.jobTitle}</>}</div>
                  {tags.length > 0 && <div className="lead-tags">{tags.map((t) => <span key={t} className="lead-tag">{t}</span>)}</div>}
                  {leadVerweise(l).length > 0 && (
                    <RelatedLinks className="mt-1" items={leadVerweise(l)} label={`Verknüpft mit ${leadName(l)}`} />
                  )}
                </td>
                <td>
                  {l.companyId
                    ? <Link to={`/companies/${l.companyId}`}><Icon name="building" size={12} /> {l.company || 'Firmenakte'}</Link>
                    : l.company
                      ? <>{l.company}<div className="text-xs text-secondary">nicht zugeordnet</div></>
                      : '—'}
                </td>
                <td>
                  <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
                  {l.clayEnrichedAt && <div className="text-xs text-secondary">Clay {datum(l.clayEnrichedAt)}</div>}
                  {l.icebreakerGeneratedAt && <div className="text-xs"><StatusBadge tone="success" icon="sparkles">Icebreaker</StatusBadge></div>}
                </td>
                <td>
                  {l.lemlistCampaignId
                    ? <>{lem ? <StatusBadge tone={lem.tone}>{lem.label}</StatusBadge> : <StatusBadge tone="success">In Kampagne</StatusBadge>}<div className="text-xs"><Link to={`/lead-sync?kampagne=${encodeURIComponent(l.lemlistCampaignId)}`} title="Nur Leads dieser Kampagne zeigen">{kampagnenName(l.lemlistCampaignId)}</Link></div></>
                    : <span className="text-secondary">—</span>}
                </td>
                <td>{quelleLabel(l.source)}</td>
                <td className="text-sm">{datum(l.createdAt)}</td>
                <td className="r">
                  <div className="btn-group lead-tabelle__aktionen">
                    {onBearbeiten && (
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => onBearbeiten(l)} aria-label={`${leadName(l)} bearbeiten`} title="Bearbeiten"><Icon name="edit" size={14} /></button>
                    )}
                    {l.lemlistCampaignId && onEntfernen && (
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => onEntfernen(l)} title="Aus der Lemlist-Kampagne entfernen">aus Kampagne</button>
                    )}
                    {onLoeschen && (
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => onLoeschen(l)} aria-label={`${leadName(l)} löschen`} title="Löschen"><Icon name="trash" size={14} /></button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
